import BaseEvent from "./base_event";
import { PlayerAhoyCommonEvent } from "../player_ahoy_common_event";
import { PlayerEnvironment } from "../player_environment";
import PerformanceAdapter from "@javascripts/adapters/performance_adapter";
import { Parser } from 'm3u8-parser';
import { fetch } from 'whatwg-fetch'
import Throttle from "@javascripts/utils/throttle";

export default class LivestreamEvent{
  constructor(options = {}){
    this._base = new BaseEvent(options);
    this._tech = options.tech;
    this._data = options.data;
    this._uuid = options.uuid;
    this._ads = options.ads;
  }

  bindEvents(){
    this.base().bindEvents();
    this.bindStart();
    this.bindPlay();
    this.bindError();
    this.bindMeta();
    this.bindBuffer();
    this.bindStuck();
    this.bindSeek();

    this.bindAdWatch();
    this.bindDownloadAppButtonClick();
    this.bindHrefButtonClick();
    this.bindReportButtonClick();
    this.bindFeedbackButtonClick();
    this.bindRetryButtonClick();
    this.bindRetrievePlaylistInSafari();
  }

  base(){
    return this._base;
  }

  tech(){
    return this._tech;
  }

  data(){
    return this._data;
  }

  uuid(){
    return this._uuid;
  }

  ads(){
    return this._ads;
  }

  _getMediaAttribute() {
    const hls = this.tech().plugin().hlsObject();
    const dash = this.tech().plugin().dashObject();

    if (dash.hasDashObject()) return dash.getCurrentMediaAttribute();
    return hls.getCurrentMediaAttribute();
  }

  bindStart(){
    this.tech().on("vp-start", (e) => {
      this.tech().trigger({
        type: "vp-livestream-start",
        id: e.id,
        embed: e.embed,
        codec: e.codec,
        streamType: this.data().streamType(),
        hasAd: e.hasAd,
        uuid: this.uuid(),
        currentTime: this.tech().currentTime(),
        readyState: this.tech().readyState(),
        networkState: this.tech().networkState(),
        bufferedVideoFrames: this.tech().bufferedVideoFrames(),
        bandwidth: this.tech().getBandwidth(),
        quality: this.tech().getQuality(),
        assetSource: this.tech().currentSrc(),
        videoSourceBitrate: e.videoSourceBitrate,
        videoSourceHeight: e.videoSourceHeight,
        videoSourceWidth: e.videoSourceWidth,
        playerHeight: e.playerHeight,
        playerWidth: e.playerWidth,
      });
    });
  }

  bindPlay(){
    let startTime;
    this.tech().one("timeupdate", function(){
      startTime = Math.floor(this.tech().currentTime());
    }.bind(this));
    var last = 0;
    var interval = this.data().sendPlayInterval();
    this.tech().on("timeupdate", function(){
      var second = Math.floor(this.tech().currentTime()) - startTime;
      if(second % interval == 0 && second > last){
        this.sendPlayEvent(second);
        last = second;
      }
    }.bind(this));
  }

  sendPlayEvent(second){
    this.tech().trigger({
      type: "vp-livestream-play",
      id: this.data().videoId(),
      seconds: second,
      embed: this.data().embed(),
      uuid: this.uuid(),
      toggle: this.data().sendPlayToggle(),
      bandwidth: this.tech().getBandwidth(),
      quality: this.tech().getQuality(),
      assetSource: this.tech().currentSrc(),
      networkState: this.tech().networkState(),
      hasAd: this.ads().paramsPresent(),
      streamType: this.data().streamType(),
      videoSourceBitrate: this.tech().getVideoSourceBitrate(),
      videoSourceHeight: this.tech().getVideoSourceHeight(),
      videoSourceWidth: this.tech().getVideoSourceWidth(),
      playerHeight: this.tech().getPlayerHeight(),
      playerWidth: this.tech().getPlayerWidth(),
    });
  }

  bindError(){
    this.tech().on("vp-error", (e) => {
      this.tech().trigger({
        type                : "vp-livestream-error",
        id                  : e.id,
        errorCode           : e.errorCode,
        errorMessage        : e.errorMessage,
        videoDuration       : e.videoDuration,
        currentTime         : e.currentTime,
        embed               : e.embed,
        uuid                : this.uuid(),
        readyState          : this.tech().readyState(),
        networkState        : this.tech().networkState(),
        bufferedVideoFrames : this.tech().bufferedVideoFrames(),
        assetSource         : this.tech().currentSrc()
      });
    });
  }

  bindMeta(){
    this.tech().on("vp-meta", (e) => {
      this.tech().trigger({
        type          : "vp-livestream-meta",
        id            : e.id,
        level         : e.level,
        levels        : e.levels,
        targetBitrate : e.targetBitrate,
        uuid          : this.uuid(),
        embed         : this.data().embed(),
        streamType    : this.data().streamType()
      });
    });
  }

  bindBuffer(){
    this.tech().on("vp-buffer", (e) => {
      this.tech().trigger({
        type          : "vp-livestream-buffer",
        id            : e.id,
        seconds       : e.seconds,
        uuid          : this.uuid(),
        timer         : e.timer,
        embed         : this.data().embed() === 'true',
        streamType    : this.data().streamType(),
        bandwidth     : this.tech().getBandwidth(),
        quality       : this.tech().getQuality(),
        assetSource   : this.tech().currentSrc(),
        videoSourceBitrate: e.videoSourceBitrate,
        videoSourceHeight: e.videoSourceHeight,
        videoSourceWidth: e.videoSourceWidth,
        playerHeight: e.playerHeight,
        playerWidth: e.playerWidth,
      });
    });
  }

  bindStuck(){
    var last = 0;
    this.tech().on("timeupdate", function(){
      var curTime = this.tech().currentTime();
      if(curTime != 0 && curTime == last){
        this.sendStuck(curTime);
      } else {
        last = curTime;
      }

    }.bind(this));
  }

  sendStuck(currentTime){
    this.tech().trigger({
      type : "vp-livestream-stuck",
      time : currentTime
    });
  }

  bindAdWatch(){
    this.tech().on("vp-adwatch", (e) => {
      this.tech().trigger({
        type        : "vp-livestream-adwatch",
        id          : e.id,
        duration    : e.duration,
        currentTime : e.currentTime,
        uuid        : this.uuid(),
        adUUID      : e.adUUID
      });
    });
  }

  bindSeek(){
    var position, action = 'seekbar';

    this.tech().on('vp-before-seeked', (e) => {
      action = e.action;
    });

    this.tech().on("timeupdate", function(){
      position = this.tech().currentTime();
    }.bind(this));

    this.tech().on("seeking", function(){
      var offset = this.tech().currentTime() - position;
      this.tech().trigger({
        type: "vp-video-seek",
        id: this.data().videoId(),
        play_uuid: this.uuid(),
        position: position,
        offset: offset,
        action: action
      });
      action = 'seekbar';
    }.bind(this));
  }

  bindDownloadAppButtonClick() {
    this.tech().on("vp-livestream-download-app-button-click", () => {
      const properties = {
        action: 'download app',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'LIVESTREAM::');
    });
  }

  bindHrefButtonClick() {
    this.tech().on("vp-livestream-href-button-click", () => {
      const properties = {
        action: 'link vidio',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'LIVESTREAM::');
    });
  }

  bindReportButtonClick() {
    this.tech().on("vp-livestream-report-button-click", () => {
      const properties = {
        action: 'send report',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'LIVESTREAM::');
    });
  }

  bindFeedbackButtonClick() {
    this.tech().on("vp-livestream-feedback-button-click", () => {
      const properties = {
        action: 'send feedback',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'LIVESTREAM::');
    });
  }

  bindRetryButtonClick() {
    this.tech().on("vp-livestream-retry-button-click", () => {
      const properties = {
        action: 'retry again',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'LIVESTREAM::');
    });
  }

  bindRetrievePlaylistInSafari() {
    if (!PlayerEnvironment.isBrowserSafari()) {
      return;
    }

    this.tech().el().el().querySelector('video').addEventListener('progress', Throttle.set(this.requestLatestClipResource.bind(this), 1000));
  }

  requestLatestClipResource() {
    const clipResource = PerformanceAdapter.getResourceTimingEntries().filter((entry) => entry.name.includes('.m3u8'));
    const latestClipResourceUrl = clipResource[clipResource.length - 1].name

    return fetch(latestClipResourceUrl)
      .then(response => response.text())
      .then(this.parsePlaylist)
      .then(this.assignToHlsPlaylistMedia.bind(this));
  }

  parsePlaylist(playlist) {
    const parser = new Parser();

    parser.push(playlist);
    parser.end();

    return parser.manifest;
  }

  assignToHlsPlaylistMedia(playlist) {
    this.tech().hlsPlaylistMedia = () => playlist
  }
}
